/**
 * Cashflow & Age Pension — the projection layer.
 *
 * WHAT THIS MODULE IS FOR (owner, 2026-08-28). Every other module in the app projects AROUND an
 * event: a move into care, a village entry, a downsize, a granny flat. This one projects a client
 * who does none of those things — they stay where they are, and the only thing that changes is what
 * they spend and how their money is arranged.
 *
 * It exists because of the Commonwealth Home Support Program. CHSP has NO MEANS TEST, so unlike
 * Support at Home there is no contribution for us to calculate — the provider sets the fee and the
 * client is told what it is. That inverts the usual job: the care cost stops being an OUTPUT we
 * compute and becomes an INPUT the client gives us. What we can then answer is the question they
 * actually have, which was never "what does CHSP cost" (they get a bill) but "can I afford this,
 * for how long, and is there anything I can do to be better off?"
 *
 * DELIBERATELY GENERAL, NARROWLY EXPOSED. Nothing here knows about CHSP, Support at Home, or how the
 * client arrived. The cost is just a recurring expense with a label. That is the owner's positioning
 * call: build it general so it can be opened from anywhere later, but for now reach it through one
 * door — the Support at Home overview — while the product decides how broad it wants to be.
 *
 * HOW A "NO EVENT" PROJECTION IS EXPRESSED, and why it is not a hack. `careStartDate` is set beyond
 * the horizon. The engine already treats that as a supported state: `careStartYearFor` returns 0,
 * documented as "never, within this run", and `careFractionOfPeriod` returns 0 for every period, so
 * no care fee is charged and none of the entry clocks (the RAD retention window, the former-home
 * exemption) ever start. Verified empirically before this module was written — a 10-year run charges
 * no care costs and pays the Age Pension on ordinary at-home rules.
 */
import * as calculationApi from '../../services/calculationApi.ts';
import type { DraftFactFind } from '../../constants/initialFactFind.ts';

/**
 * A recurring cost the client tells us about — CHSP fees, a cleaner, private help at home.
 *
 * NOT a fact-find field, by owner direction. It lives in module state the way the Downsizer holds a
 * sale price, so no schema change is needed and the figure stays visibly the CLIENT'S rather than
 * ours. It is only persisted if they save a strategy.
 */
export interface SupportCostInput {
    /** Annual amount in dollars. */
    annual: number;
    /** What it is, in the client's words — shown back to them so the figure keeps its provenance. */
    label: string;
}

/**
 * One line of an engine breakdown.
 *
 * The engine emits these as ORDERED ARRAYS, not keyed objects — a working you read top to bottom,
 * with `--- Asset Test ---` style separator rows and values that are sometimes already-formatted
 * strings ("$45,000", "Part Pension") and sometimes raw numbers. Reading them as a keyed object was
 * my first mistake here: every lookup missed and the card would have rendered a column of zeros
 * with all the authority of a calculation.
 */
export interface BreakdownRow {
    label: string;
    value: number | string;
    emphasis?: boolean;
    /** Marks a row that EXPLAINS the figures without being one of them — never sum it. */
    note?: boolean;
}

/** One projected year, as this module reads it. Keys are the engine's own. */
export interface CashflowYear {
    year: number;
    /** Age Pension for the period. */
    socialSecurity: number;
    socialSecurityBreakdown?: BreakdownRow[];
    cashFlow: number;
    cashFlowBreakdown?: BreakdownRow[];
    netWorth: number;
    netWorthBreakdown?: BreakdownRow[];
    livingExpenses: number;
    investmentIncome: number;
    abpIncome?: number;
    rentalIncome?: number;
    tax: number;
    taxBreakdown?: BreakdownRow[];
    /** Set where the plan cannot be funded from income and drawdown — the runway signal. */
    unfundedShortfall?: number;
    fyLabel?: string;
    [key: string]: unknown;
}

export interface RunOptions {
    legacyYears?: number;
    /** Added to living expenses for the run. Absent → the client's position as it stands. */
    supportCost?: SupportCostInput | null;
    inflationAssumptionPct?: number;
}

/**
 * A care date far enough out that no projected period can contain it.
 *
 * Not "no date at all": a null `careStartDate` means "already in care as the run opens", which is
 * the legacy reading and the opposite of what this module models. The date has to be present and
 * unreachable.
 */
const NEVER = '2199-01-01';

/** The fee schedule for a client who never enters residential care — every rate nil. */
const noCareCosts = (scenarioLivingExpenses: number) => ({
    totalDailyCost: 0,
    dailyAccommodationPayment: 0,
    basicDailyFee: 0,
    dailyNCCC: 0,
    dailyHSC: 0,
    dailyExtraServiceFee: 0,
    scenarioLivingExpenses,
});

/**
 * The client's living expenses for a run, with any support cost folded in.
 *
 * Folded rather than modelled separately because the engine has one recurring-expense channel
 * (`expenses.livingExpenses`); `capitalExpenses` are one-offs. The module keeps the two APART on
 * screen so the client can always see what the support is costing them — the arithmetic is joined,
 * the presentation is not.
 */
export function livingExpensesWithSupport(factFind: DraftFactFind, supportCost?: SupportCostInput | null): number {
    const base = parseFloat(String(factFind?.expenses?.livingExpenses ?? '')) || 0;
    return base + Math.max(0, supportCost?.annual || 0);
}

/**
 * Project the client forward with no care event.
 *
 * Passing `supportCost` runs the WITH-cost world; omitting it runs their position as it stands. The
 * two are otherwise identical, so any difference between them is the support cost and nothing else.
 */
export async function runCashflowProjections(
    factFind: DraftFactFind,
    options: RunOptions = {},
): Promise<CashflowYear[]> {
    const legacyYears = options.legacyYears || 10;
    const scenarioLivingExpenses = livingExpensesWithSupport(factFind, options.supportCost);
    const simulated: DraftFactFind = {
        ...factFind,
        expenses: { ...(factFind.expenses || {}), livingExpenses: scenarioLivingExpenses },
    } as DraftFactFind;

    const result = await calculationApi.runProjections(
        simulated,
        {
            scenarioLivingExpenses,
            careStartDate: NEVER,
            ...(options.inflationAssumptionPct != null ? { inflationAssumptionPct: options.inflationAssumptionPct } : {}),
        } as never,
        noCareCosts(scenarioLivingExpenses) as never,
        legacyYears,
    );
    return (result || []) as CashflowYear[];
}

/** The headline figures a client reads first, taken from the first projected year and the last. */
export interface PositionSummary {
    agePension: number;
    cashFlow: number;
    netWorth: number;
    estate: number;
    /** The first projected year the plan cannot be funded, or null if it never happens. */
    shortfallYear: number | null;
    livingExpenses: number;
    totalIncome: number;
}

export function summarisePosition(years: CashflowYear[]): PositionSummary | null {
    if (!years?.length) return null;
    const first = years[0];
    const last = years[years.length - 1];
    /*
     * The runway. `unfundedShortfall` is the engine's own signal that a year's plan could not be
     * paid for out of income plus drawdown — the honest answer to "how long does this last?", and
     * far more useful to a client than a net-worth line that quietly goes to zero.
     */
    const shortfall = years.find((y) => (Number(y.unfundedShortfall) || 0) > 0);
    return {
        agePension: Number(first.socialSecurity) || 0,
        cashFlow: Number(first.cashFlow) || 0,
        netWorth: Number(first.netWorth) || 0,
        estate: Number(last.netWorth) || 0,
        shortfallYear: shortfall ? Number(shortfall.year) : null,
        livingExpenses: Number(first.livingExpenses) || 0,
        totalIncome:
            (Number(first.investmentIncome) || 0) +
            (Number(first.abpIncome) || 0) +
            (Number(first.rentalIncome) || 0) +
            (Number(first.socialSecurity) || 0),
    };
}

/** What a support cost does — the same projection run twice, differing only in that cost. */
export interface SupportCostImpact {
    without: PositionSummary;
    with: PositionSummary;
    /** Negative: the estate is smaller for having paid the cost. */
    estateChange: number;
    cashFlowChange: number;
    /** Age Pension is means tested on assets and income, not on what you spend — expected to be nil. */
    pensionChange: number;
}

export async function compareWithAndWithoutSupport(
    factFind: DraftFactFind,
    supportCost: SupportCostInput,
    legacyYears = 10,
): Promise<SupportCostImpact | null> {
    const [without, withCost] = await Promise.all([
        runCashflowProjections(factFind, { legacyYears }),
        runCashflowProjections(factFind, { legacyYears, supportCost }),
    ]);
    const a = summarisePosition(without);
    const b = summarisePosition(withCost);
    if (!a || !b) return null;
    return {
        without: a,
        with: b,
        estateChange: b.estate - a.estate,
        cashFlowChange: b.cashFlow - a.cashFlow,
        pensionChange: b.agePension - a.agePension,
    };
}

/**
 * One evaluated candidate, as the server returns it.
 *
 * Mirrors CashflowPensionResult in server/.../suggestionEngine/cashflowPension.ts — the frontend
 * cannot import server code, so this is the boundary contract. `versusBaseline` is the figure the
 * cards actually show: everything is stated as a change against doing nothing.
 */
export interface CashflowPensionResult {
    id: string;
    name: string;
    description: string;
    changeNothing: boolean;
    projections: CashflowYear[];
    snapshot: {
        pension: number;
        cashflow: number;
        estate: number;
        shortfallYear: number | null;
    };
    versusBaseline?: {
        pension: number;
        cashflow: number;
        estate: number;
    };
    /**
     * The terms the annuity was built on, where this option buys one — owner, residual capital,
     * reversionary, indexation. Reported by the engine rather than restated by the screen, so the
     * disclosure and the arithmetic cannot drift apart.
     */
    annuityTerms?: Record<string, unknown> | null;

    error?: string;
}

/**
 * What the client has chosen to move, in Manual Control.
 *
 * THE DIFFERENCE FROM EVERY OTHER MODULE'S ALLOCATOR, and it matters. Elsewhere Manual Control
 * deploys PROCEEDS — a lump sum that a sale has just created, sitting outside the balance sheet
 * waiting to be placed. Here there is no sale and no new money. The client is REARRANGING what they
 * already own, so the pool is their existing liquid assets and every dollar moved has to come out of
 * something they currently hold.
 *
 * That is why this is its own shape rather than ProceedsDeploymentFields: reusing an allocator built
 * around "here is $400,000, where does it go?" would have modelled money appearing from nowhere.
 */
export interface ManualAllocation {
    /** Into a funeral bond — exempt up to the published limit per person. */
    funeralBond: number;
    /** Given away — outside the free areas it is still counted for five years. */
    gift: number;
    /** Into a lifetime annuity — 60% of the purchase price is assessed. */
    annuity: number;
    /** Off a debt — reduces what is owed and frees the repayments. */
    debtRepayment: number;
}

export const EMPTY_ALLOCATION: ManualAllocation = { funeralBond: 0, gift: 0, annuity: 0, debtRepayment: 0 };

/**
 * How the client wants the annuity STRUCTURED, kept apart from how much goes into it.
 *
 * OWNER-DIRECTED, 2026-09-01: every other module lets a client set the owner, the residual capital,
 * whether it is reversionary and whether it is indexed — this one hard-coded all four and offered a
 * slider. The amount stays in `ManualAllocation` because `allocationTotal` sums that map and a
 * non-numeric field in it would corrupt the total; the terms live here instead.
 */
export interface ManualAnnuityTerms {
    /** 0 to 1. Nil pays the most income and leaves nothing; 1 returns the purchase price. */
    residualCapital: number;
    owner: 'client' | 'partner' | 'joint';
    reversionary: boolean;
    indexation: boolean;
    /** A real provider quote, replacing the indicative rate. */
    incomeOverride: number | null;
}

export const DEFAULT_ANNUITY_TERMS: ManualAnnuityTerms = {
    /*
     * Capital preserved, like every other module's Manual Control — see
     * DEFAULT_NEW_ANNUITY_RESIDUAL in SupportAtHomeAnnuityConfigurator.tsx for why the default is
     * the option that does NOT commit the client's money permanently. The means test is identical
     * either way, so the default costs nothing and the client still chooses.
     */
    residualCapital: 1,
    owner: 'client',
    reversionary: false,
    indexation: false,
    incomeOverride: null,
};

/** Every liquid pot the client could actually move money out of. */
export interface LiquidSource {
    type: string;
    id: string;
    description: string;
    value: number;
}

export function liquidSourcesOf(factFind: DraftFactFind): LiquidSource[] {
    const assets: any = factFind?.assets || {};
    const collect = (type: string) =>
        (assets[type] || [])
            .filter((row: any) => row?.id && (parseFloat(String(row?.value ?? '')) || 0) > 0)
            .map((row: any) => ({
                type,
                id: String(row.id),
                description: String(row.description || type),
                value: parseFloat(String(row.value)) || 0,
            }));
    return [...collect('bankAccounts'), ...collect('termDeposits'), ...collect('shares')];
}

export function totalLiquid(factFind: DraftFactFind): number {
    return liquidSourcesOf(factFind).reduce((sum, s) => sum + s.value, 0);
}

export function allocationTotal(allocation: ManualAllocation): number {
    return Math.max(0, allocation.funeralBond) + Math.max(0, allocation.gift)
        + Math.max(0, allocation.annuity) + Math.max(0, allocation.debtRepayment);
}

/**
 * Turn an allocation into the funding plan the engine's levers require, drawing bank first.
 *
 * Bank before shares because selling shares can crystallise capital gains tax that this module does
 * not model — proposing it silently would understate the cost of the move.
 */
export function fundingPlanFor(sources: LiquidSource[], amount: number): Array<{ type: string; id: string; amountToTake: number }> {
    const plan: Array<{ type: string; id: string; amountToTake: number }> = [];
    let remaining = Math.max(0, amount);
    for (const source of sources) {
        if (remaining <= 0) break;
        const take = Math.min(source.value, remaining);
        if (take > 0) plan.push({ type: source.type, id: source.id, amountToTake: take });
        remaining -= take;
    }
    return plan;
}

/**
 * A manual allocation as the SAME candidate shape the engine ranks (the page evaluates it with the
 * engine's own call). Kept here so the page and the Action Plan's after-the-plan forms build one
 * plan from one allocation.
 */
export function manualCandidateFrom(allocation: ManualAllocation, annuityTerms: ManualAnnuityTerms, sources: LiquidSource[]) {
    const plan = fundingPlanFor(sources, allocationTotal(allocation));
    return {
        id: 'cp-manual',
        name: 'Your plan',
        description: 'The allocation you have entered above.',
        ...(allocation.funeralBond > 0 ? { funeralBondPurchase: allocation.funeralBond } : {}),
        ...(allocation.gift > 0 ? { giftingAmount: allocation.gift } : {}),
        ...(allocation.annuity > 0
            /*
             * `ownership`, NOT `owner`. The engine's annuityTermsFor reads `ownership`; this
             * passed `owner`, which it read straight past — so a partner- or jointly-owned
             * annuity would have been built in the client's name whatever the client chose
             * (2026-09-01). It accepts both spellings now, and this sends the one the rest of
             * the engines send.
             */
            ? {
                  annuity: {
                      purchaseAmount: allocation.annuity,
                      annuityType: 'lifetime',
                      ownership: annuityTerms.owner,
                      residualCapital: annuityTerms.residualCapital,
                      reversionary: annuityTerms.reversionary,
                      indexation: annuityTerms.indexation,
                      incomeOverride: annuityTerms.incomeOverride,
                  },
              }
            : {}),
        fundingSources: plan,
    };
}
